if (!customElements.get('bogo-most-love-section')) {
class BogoMostLoveSection extends HTMLElement {
  connectedCallback() {
    this.switcher = this.querySelector('[data-switcher-track]');
    this.panels = [...this.querySelectorAll('[data-panel]')];

    this.initSwitcher();
  }

  disconnectedCallback() {
    this._teardownScrollMode?.();
    this._teardownInfiniteMode?.();
  }

  initSwitcher() {
    if (!this.switcher || !this.panels.length) return;

    this._switcherItems = [...this.switcher.querySelectorAll('[data-switcher-item]')];
    if (!this._switcherItems.length) return;

    const infiniteScrollRequested = this.switcher.dataset.infiniteScroll === 'true';
    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    if (infiniteScrollRequested && isMobile && this._switcherItems.length >= 2) {
      this.initInfiniteMode();
    } else {
      this.initScrollMode();
    }
  }

  getDefaultActiveItem() {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const raw = isMobile ? this.dataset.activeIndexMobile : this.dataset.activeIndexDesktop;
    const oneBased = parseInt(raw, 10);

    if (Number.isInteger(oneBased) && oneBased >= 1) {
      const byBlockPosition = this._switcherItems.find((item) => parseInt(item.dataset.blockPosition, 10) === oneBased);
      if (byBlockPosition) return byBlockPosition;

      if (oneBased <= this._switcherItems.length) {
        return this._switcherItems[oneBased - 1];
      }
    }

    const middleIndex = Math.floor((this._switcherItems.length - 1) / 2);
    return this._switcherItems[middleIndex];
  }

  showPanelFor(panelIndex) {
    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.panelIndex !== panelIndex;
    });
  }

  /* =========================================================
     Scroll mode — default (non-infinite) switcher. Native
     overflow-x:auto + scroll-snap, unchanged.
     ========================================================= */

  initScrollMode() {
    this._infiniteMode = false;

    this._switcherClickHandlers = this._switcherItems.map((item) => {
      const handler = () => this.activatePanel(item.dataset.panelIndex, true);
      item.addEventListener('click', handler);
      return handler;
    });

    this._switcherFocusHandlers = this._switcherItems.map((item) => {
      const handler = () => this.activatePanel(item.dataset.panelIndex, true);
      item.addEventListener('focus', handler);
      return handler;
    });

    this._onResize = () => this.centerActiveSwitcherItem(false);
    window.addEventListener('resize', this._onResize);

    this._onSwitcherScroll = () => {
      if (this._isProgrammaticScroll) return;
      clearTimeout(this._scrollSettleTimer);
      this._scrollSettleTimer = setTimeout(() => this.syncActivePanelToScrollPosition(), 120);
    };
    this.switcher.addEventListener('scroll', this._onSwitcherScroll, { passive: true });

    this.applyDefaultActiveItem();

    this._onWindowLoad = () => {
      if (!this._switcherItems.some((item) => item.classList.contains('is-active'))) {
        this.applyDefaultActiveItem();
      } else {
        this.centerActiveSwitcherItem(false);
      }
    };
    if (document.readyState === 'complete') {
      this._onWindowLoad();
    } else {
      window.addEventListener('load', this._onWindowLoad);
    }

    this._teardownScrollMode = () => {
      this._switcherItems.forEach((item, i) => {
        item.removeEventListener('click', this._switcherClickHandlers[i]);
        item.removeEventListener('focus', this._switcherFocusHandlers[i]);
      });
      this.switcher.removeEventListener('scroll', this._onSwitcherScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onWindowLoad);
      clearTimeout(this._scrollSettleTimer);
      clearTimeout(this._programmaticScrollTimer);
    };
  }

  applyDefaultActiveItem() {
    try {
      const defaultItem = this.getDefaultActiveItem();
      if (defaultItem) this.activatePanel(defaultItem.dataset.panelIndex, false);
    } catch (error) {
      console.error('bogo-most-love-section: failed to apply default active item', error);
    }
  }

  syncActivePanelToScrollPosition() {
    const trackRect = this.switcher.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;

    let closestItem = null;
    let closestDistance = Infinity;

    this._switcherItems.forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenter - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestItem = item;
      }
    });

    if (closestItem && !closestItem.classList.contains('is-active')) {
      this.activatePanel(closestItem.dataset.panelIndex, true);
    }
  }

  activatePanel(panelIndex, smoothScroll) {
    if (panelIndex == null) return;

    this.showPanelFor(panelIndex);

    let activeItem = null;
    this._switcherItems.forEach((item) => {
      const isActive = item.dataset.panelIndex === panelIndex;
      item.classList.toggle('is-active', isActive);
      if (isActive) activeItem = item;
    });

    if (activeItem) this.centerSwitcherItem(activeItem, smoothScroll);
  }

  centerActiveSwitcherItem(smoothScroll) {
    const activeItem = this._switcherItems?.find((item) => item.classList.contains('is-active'));
    if (activeItem) this.centerSwitcherItem(activeItem, smoothScroll);
  }

  centerSwitcherItem(item, smoothScroll) {
    this._isProgrammaticScroll = true;
    clearTimeout(this._programmaticScrollTimer);

    const clearFlag = () => {
      clearTimeout(this._programmaticScrollTimer);
      this.switcher.removeEventListener('scrollend', clearFlag);
      this._isProgrammaticScroll = false;
    };
    this.switcher.addEventListener('scrollend', clearFlag, { once: true });
    this._programmaticScrollTimer = setTimeout(clearFlag, smoothScroll ? 1000 : 50);

    item.scrollIntoView({
      behavior: smoothScroll ? 'smooth' : 'instant',
      inline: 'center',
      block: 'nearest',
    });
  }

  /* =========================================================
     Infinite mode — real jQuery Slick Carousel (Center Mode),
     the theme's only jQuery dependency, scoped entirely to this
     opt-in setting. Slick owns cloning, centering, drag/swipe
     and the loop-wrap internally; this only has to wire its
     events back into showPanelFor and honor the schema's
     default-active-item + click-to-recenter behavior.
     ========================================================= */

  initInfiniteMode() {
    const $ = window.jQuery;
    if (!$ || !$.fn || !$.fn.slick) {
      console.error('bogo-most-love-section: jQuery/Slick not loaded, falling back to scroll mode');
      this.initScrollMode();
      return;
    }

    this._infiniteMode = true;
    this.switcher.querySelectorAll('.bogo-most-love-section__switcher-spacer').forEach((el) => el.remove());

    const $switcher = $(this.switcher);
    this._$switcher = $switcher;

    const defaultItem = this.getDefaultActiveItem();
    const initialSlide = defaultItem ? Math.max(this._switcherItems.indexOf(defaultItem), 0) : 0;

    this._onSlickChange = (_event, slick, currentSlide) => {
      const idx = typeof currentSlide === 'number' ? currentSlide : slick.currentSlide;
      const activeSlide = slick.$slides.get(idx);
      const panelIndex = activeSlide?.dataset.panelIndex;
      if (panelIndex != null) this.showPanelFor(panelIndex);
    };
    $switcher.on('init afterChange', this._onSlickChange);

    // Slick's centerMode doesn't recenter a clicked, currently-off-center
    // slide on its own — slickGoTo does, using the index Slick stamped onto
    // each .slick-slide wrapper it built around our real .switcher-item.
    this._onSlideClick = (event) => {
      const $slide = $(event.currentTarget).closest('.slick-slide');
      const idx = $slide.data('slick-index');
      if (typeof idx === 'number') $switcher.slick('slickGoTo', idx);
    };
    $switcher.on('click', '.bogo-most-love-section__switcher-item', this._onSlideClick);

    // Mirrors this section's own CSS breakpoints (768/480/390 — see
    // bogo-most-love-section.css) so Slick's own responsive re-layout stays
    // in step with them instead of just the single base config. Slick only
    // ever initializes at ≤768px in the first place (see initSwitcher), so
    // 480 and 390 are the only sub-breakpoints that matter here.
    $switcher.slick({
      centerMode: true,
      centerPadding: '0px',
      infinite: true,
      slidesToShow: 5,
      slidesToScroll: 1,
      arrows: false,
      dots: false,
      autoplay: false,
      draggable: true,
      swipe: true,
      speed: 350,
      initialSlide,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            centerMode: true,
            centerPadding: '0px',
            infinite: true,
            slidesToShow: 3,
            slidesToScroll: 1,
            arrows: false,
            dots: false,
          },
        },
        {
          breakpoint: 480,
          settings: {
            centerMode: true,
            centerPadding: '0px',
            infinite: true,
            slidesToShow: 3,
            slidesToScroll: 1,
            arrows: false,
            dots: false,
          },
        },
        {
          breakpoint: 390,
          settings: {
            centerMode: true,
            centerPadding: '0px',
            infinite: true,
            slidesToShow: 3,
            slidesToScroll: 1,
            arrows: false,
            dots: false,
          },
        },
      ],
    });

    this._teardownInfiniteMode = () => {
      $switcher.off('init afterChange', this._onSlickChange);
      $switcher.off('click', '.bogo-most-love-section__switcher-item', this._onSlideClick);
      if ($switcher.hasClass('slick-initialized')) $switcher.slick('unslick');
    };
  }
}

customElements.define('bogo-most-love-section', BogoMostLoveSection);
}
